import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { CryptoIcon } from '@sorare/core/src/atoms/icons/CryptoIcon';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { cardRatio } from '@sorare/core/src/lib/cardPicture';
import { isCryptocurrency } from '@sorare/core/src/lib/cryptocurrency';
import {
  faFiatCurrencySymbols,
  isFiatCurrency,
} from '@sorare/core/src/lib/fiat';
import type {
  MonetaryAmount,
  MonetaryAmountParams,
} from '@sorare/core/src/lib/monetaryAmount';

import { FeesDetailsTooltip } from '#src/components/offer/FeesDetailsTooltip/index.tsx';
import { MarketFeeStatus } from '#src/hooks/useMarketFeesHelperStatus.ts';

const Card = styled(Horizontal).attrs({ gap: 0 })`
  justify-content: start;
`;

const MonetaryImageContainer = styled.div<{ isFiat: boolean }>`
  width: 40px;
  aspect-ratio: ${cardRatio};
  background-color: ${({ isFiat }) =>
    isFiat ? 'var(--c-onBase-success)' : 'rgba(var(--c-rgb-blue-400), 0.25)'};

  margin-right: var(--unit);
  border-radius: var(--half-unit);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--unit) 0;
`;

const Amount = styled.div`
  display: flex;
  align-items: baseline;
  gap: var(--unit);
`;

const Fees = styled(Horizontal)`
  color: var(--c-nd-600);
`;

type Props = {
  amount: MonetaryAmountParams;
  displayFees?: boolean;
  marketFeeAmount?: MonetaryAmount;
  marketFeeStatus?: MarketFeeStatus;
};

export const MonetaryCard = ({
  amount,
  marketFeeAmount,
  displayFees,
  marketFeeStatus,
}: Props) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const { fiatCurrency } = useCurrentUserCurrencies();
  const {
    walletPreferences: { onlyShowFiatCurrency },
  } = useCurrentUserContext();

  if (!amount) {
    return null;
  }

  const { referenceCurrency } = amount;
  const isFiat = isFiatCurrency(referenceCurrency);
  const feesDisplayable = displayFees && marketFeeAmount?.isPositive;

  return (
    <Card>
      <MonetaryImageContainer isFiat={isFiat}>
        {isFiat ? (
          <FontAwesomeIcon
            size="lg"
            color="var(--c-white)"
            icon={faFiatCurrencySymbols[fiatCurrency]}
          />
        ) : (
          <CryptoIcon cryptocurrency={referenceCurrency} />
        )}
      </MonetaryImageContainer>
      <Amount>
        <AmountWithConversion
          monetaryAmount={amount}
          primaryCurrency={
            isFiatCurrency(referenceCurrency) ? Currency.FIAT : Currency.ETH
          }
          hideExponent={onlyShowFiatCurrency}
          column
          forceCryptocurrency={
            isCryptocurrency(amount.referenceCurrency)
              ? amount.referenceCurrency
              : undefined
          }
        />
        {!!feesDisplayable && (
          <Fees>
            <FeesDetailsTooltip
              monetaryAmount={toMonetaryAmount(amount)}
              marketFeeMonetaryAmount={marketFeeAmount}
              referenceCurrency={amount.referenceCurrency}
              marketFeeStatus={
                marketFeeStatus !== MarketFeeStatus.DISABLED
                  ? marketFeeStatus
                  : undefined
              }
            />
          </Fees>
        )}
        {!!feesDisplayable && (
          <LabelS>
            <FormattedMessage
              id="EthereumCard.marketFeesApply"
              defaultMessage="market fees apply"
            />
          </LabelS>
        )}
      </Amount>
    </Card>
  );
};
